import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type { LanguageEnum, OneTimePasswordRequest, TermsData } from '@apiTypes'
import { defaultLanguage } from '@/plugins/i18n'

interface CarrierPortalState {
  carrierEmail?: string
  otpHash?: string
  preferredLanguage: LanguageEnum
  termsData?: TermsData
  expiresAt?: string
}

export const useCarrierPortalStore = defineStore('carrierOtp', {
  state: (): CarrierPortalState => ({
    carrierEmail: undefined,
    otpHash: undefined,
    preferredLanguage: defaultLanguage,
    termsData: undefined,
    expiresAt: undefined,
  }),
  getters: {
    getCarrierEmail: state => { return state.carrierEmail || '' },
    getOtpHash: state => { return state.otpHash || '' },
    getCurrentLanguage: state => { return state.preferredLanguage || '' },
  },
  actions: {
    setCarrierEmail(email: string) {
      this.carrierEmail = email
    },
    setOtpHash(otpHash: string) {
      this.otpHash = otpHash
    },
    setPreferredLanguage(preferredLanguage: LanguageEnum) {
      this.preferredLanguage = preferredLanguage
    },
    clearOtp() {
      this.carrierEmail = undefined
      this.otpHash = undefined
      this.expiresAt = undefined
      this.preferredLanguage = defaultLanguage
    },
    updateLanguage(otpHash: string, preferredLanguage: LanguageEnum) {
      return api.otp.otpSetLanguage({ otpHash }, { preferredLanguage }).then(response => {
        this.setPreferredLanguage(response.data.preferredLanguage ?? defaultLanguage)
      })
    },
    fetchOneTimePassword(otpHash: string) {
      return api.otp.otpRetrieve({ otpHash }).then(response => {
        this.setCarrierEmail(response.data.email)
        this.setOtpHash(response.data.hash || '')
        this.setPreferredLanguage(response.data.preferredLanguage ?? defaultLanguage)
        this.termsData = response.data.termsData
        this.expiresAt = response.data.expiresAt

        return response
      })
    },
    requestOneTimePassword(data: OneTimePasswordRequest) {
      this.clearOtp()

      return api.otp.otpCreate(data)
    },
    requestSessionRenewal(email: string) {
      return api.otp.otpCreate({ email })
    },
    invalidateOneTimePassword(otpHash: string) {
      this.clearOtp()

      return api.otp.otpExpireCreate({ otpHash: otpHash })
    },
    getUnpaidInvoices(otpHash: string) {
      return api.otp.otpUnpaidInvoicesRetrieve({ otpHash })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCarrierPortalStore, import.meta.hot))
}
